// Path: ./src/app/components/FormKit/styled.ts
import styled from "styled-components";

export const token = {
  space: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  color: {
    surface: "#ffffff",
    canvas: "#f6f7f9",
    border: "#e2e5e9",
    borderStrong: "#c6cad0",
    text: "#191c20",
    textMuted: "#6b7280",
    accent: "#f58220",
    accentHover: "#e4711f",
    accentSoft: "#fff6ef",
    danger: "#d32f2f",
  },
  radius: { sm: "6px", md: "8px", lg: "12px" },
  controlHeight: 40,
} as const;

export const PageShell = styled.div`
  margin-top: 65px;
  min-height: 100%;
  background: ${token.color.canvas};
`;

export const FormShell = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: ${token.space.xl} ${token.space.lg} ${token.space.xxl};

  @media (max-width: 600px) {
    padding: ${token.space.lg} ${token.space.md} ${token.space.xl};
  }
`;

export const SectionBox = styled.section`
  background: ${token.color.surface};
  border: 1px solid ${token.color.border};
  border-radius: ${token.radius.lg};
  padding: ${token.space.xl};
  margin-bottom: ${token.space.lg};

  @media (max-width: 600px) {
    padding: ${token.space.lg};
  }
`;

export const SectionHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${token.space.md};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  user-select: none;
`;

export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${token.space.md};
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${token.color.text};

  &:before {
    content: "";
    width: 3px;
    height: 18px;
    border-radius: 2px;
    background: ${token.color.accent};
  }
`;

export const SectionBody = styled.div`
  padding-top: ${token.space.xl};
`;

export const Callout = styled.p`
  margin: 0 0 ${token.space.lg};
  padding: ${token.space.md} ${token.space.lg};
  background: ${token.color.accentSoft};
  border: 1px solid #f7ddc6;
  border-radius: ${token.radius.md};
  font-size: 13px;
  line-height: 1.55;
  color: #7a5230;
`;

export const FieldRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${token.space.xs};
  width: 100%;
`;

export const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${token.space.xs};
  min-height: 18px;
`;

export const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${token.color.textMuted};
`;

export const RequiredMark = styled.span`
  color: ${token.color.accent};
  margin-left: 2px;
  font-weight: 700;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const FieldFooter = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${token.space.sm};
  min-height: 16px;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) =>
    props.$error ? token.color.danger : token.color.textMuted};
`;

export const FieldCounter = styled.span<{ $over?: boolean }>`
  flex: none;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$over ? token.color.danger : token.color.textMuted};
`;

export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  height: ${token.controlHeight}px;
`;

export const ReadOnlyValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${token.space.sm};
  height: ${token.controlHeight}px;
  padding: 0 ${token.space.md};
  border: 1px dashed ${token.color.border};
  border-radius: ${token.radius.sm};
  background: ${token.color.canvas};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  color: ${token.color.text};
`;

export const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${token.space.md};
  margin-top: ${token.space.xl};
  padding-top: ${token.space.lg};
  border-top: 1px solid ${token.color.border};
`;
